/**
 * Iframe Authentication Utility
 * Handles cross-origin communication with parent window for shared authentication
 * 
 * Since AppyPie proxies iframes, we use postMessage to request tokens from parent.
 */

const MESSAGE_TYPES = {
  AUTH_LOGIN: 'AUTH_LOGIN',
  AUTH_LOGOUT: 'AUTH_LOGOUT',
  AUTH_REQUEST: 'AUTH_REQUEST',
  AUTH_RESPONSE: 'AUTH_RESPONSE',
  AUTH_LOGOUT_CHECK: 'AUTH_LOGOUT_CHECK',
  AUTH_LOGOUT_RESPONSE: 'AUTH_LOGOUT_RESPONSE',
};

const REQUEST_TIMEOUT = 5000; // 5 seconds

/**
 * Get the parent window origin for validation
 * @returns {string}
 */
const getParentOrigin = () => {
  // Parent origin should be validated - for now, accept any origin
  // In production, you may want to whitelist specific origins
  return '*'; // Accept messages from any origin (parent should validate)
};

/**
 * Request authentication token from parent window
 * Called when iframe loads and needs to check for existing auth
 * @returns {Promise<string|null>} The refresh token or null if not available
 */
export const requestAuthFromParent = () => {
  return new Promise((resolve) => {
    // Set up listener for parent response
    const messageHandler = (event) => {
      if (event.data && event.data.type === MESSAGE_TYPES.AUTH_RESPONSE) {
        window.removeEventListener('message', messageHandler);
        clearTimeout(timeoutId);
        const token = event.data.token || null;
        console.log('[iframeAuth] Received auth token from parent:', token ? 'yes' : 'no');
        resolve(token);
      }
    };

    window.addEventListener('message', messageHandler);

    // Send request to parent
    try {
      const message = {
        type: MESSAGE_TYPES.AUTH_REQUEST,
      };

      window.parent.postMessage(message, getParentOrigin());
      console.log('[iframeAuth] Requested auth token from parent');
    } catch (error) {
      console.error('[iframeAuth] Error requesting auth from parent:', error);
      window.removeEventListener('message', messageHandler);
      resolve(null);
      return;
    }

    // Timeout after REQUEST_TIMEOUT
    const timeoutId = setTimeout(() => {
      window.removeEventListener('message', messageHandler);
      console.warn('[iframeAuth] Request timeout - no response from parent');
      resolve(null);
    }, REQUEST_TIMEOUT);
  });
};

/**
 * Send authentication token to parent window
 * Called after successful login or handoff so parent can store the token
 * @param {string} refreshToken - The refresh token to share with parent
 */
export const sendAuthToParent = (refreshToken) => {
  if (!refreshToken) {
    return;
  }

  try {
    const message = {
      type: MESSAGE_TYPES.AUTH_LOGIN,
      token: refreshToken,
    };

    window.parent.postMessage(message, getParentOrigin());
    console.log('[iframeAuth] Sent auth token to parent');
  } catch (error) {
    console.error('[iframeAuth] Error sending auth to parent:', error);
  }
};

/**
 * Notify parent window that user has logged out
 * Called when user logs out so parent can clear the stored token and set logout flag
 */
export const notifyParentLogout = () => {
  try {
    const message = {
      type: MESSAGE_TYPES.AUTH_LOGOUT,
    };

    window.parent.postMessage(message, getParentOrigin());
    console.log('[iframeAuth] Notified parent of logout');
  } catch (error) {
    console.error('[iframeAuth] Error notifying parent of logout:', error);
  }
};

/**
 * Check if logout flag is set in parent window
 * Called when iframe loads to check if user was logged out from another frontend
 * @returns {Promise<boolean>} True if logout flag is set, false otherwise
 */
export const checkLogoutFromParent = () => {
  return new Promise((resolve) => {
    // Set up listener for parent response
    const messageHandler = (event) => {
      if (event.data && event.data.type === MESSAGE_TYPES.AUTH_LOGOUT_RESPONSE) {
        window.removeEventListener('message', messageHandler);
        clearTimeout(timeoutId);
        const shouldLogout = event.data.shouldLogout || false;
        console.log('[iframeAuth] Received logout check response from parent:', shouldLogout);
        resolve(shouldLogout);
      }
    };

    window.addEventListener('message', messageHandler);

    // Send request to parent
    try {
      const message = {
        type: MESSAGE_TYPES.AUTH_LOGOUT_CHECK,
      };

      window.parent.postMessage(message, getParentOrigin());
      console.log('[iframeAuth] Requested logout check from parent');
    } catch (error) {
      console.error('[iframeAuth] Error requesting logout check from parent:', error);
      window.removeEventListener('message', messageHandler);
      resolve(false);
      return;
    }

    // Timeout after REQUEST_TIMEOUT
    const timeoutId = setTimeout(() => {
      window.removeEventListener('message', messageHandler);
      console.warn('[iframeAuth] Logout check timeout - no response from parent');
      resolve(false);
    }, REQUEST_TIMEOUT);
  });
};
